import React from "react";

export function Card({ children, style }) {
  return <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: "20px", ...style }}>{children}</div>;
}

export function StatCard({ label, value, icon, accent }) {
  return (
    <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: "20px 24px", display: "flex", alignItems: "center", gap: 16 }}>
      <div style={{ width: 44, height: 44, borderRadius: 10, background: accent ? `rgba(${accent}, 0.12)` : "var(--bg-elevated)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, fontFamily: "var(--font-mono)" }}>{value}</div>
        <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

export function Button({ children, onClick, variant = "primary", type = "button", disabled, style }) {
  const variants = {
    primary: { background: "var(--accent)", color: "#000", border: "1px solid var(--accent)" },
    secondary: { background: "transparent", color: "var(--text-secondary)", border: "1px solid var(--border-light)" },
    danger: { background: "transparent", color: "var(--accent-red)", border: "1px solid rgba(255,77,77,0.3)" },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      style={{ padding: "9px 18px", borderRadius: "var(--radius)", fontSize: 13, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, fontFamily: "var(--font-body)", transition: "all 0.15s", ...variants[variant], ...style }}>
      {children}
    </button>
  );
}

export function Input({ label, name, value, onChange, placeholder, required, type = "text" }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label && <label style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label} {required && <span style={{ color: "var(--accent-red)" }}>*</span>}</label>}
      <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder} required={required}
        style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "10px 14px", color: "var(--text-primary)", fontSize: 14, fontFamily: "var(--font-body)", outline: "none", width: "100%" }}
        onFocus={e => e.target.style.borderColor = "var(--accent)"}
        onBlur={e => e.target.style.borderColor = "var(--border)"} />
    </div>
  );
}

export function Select({ label, name, value, onChange, options, required }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label && <label style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label} {required && <span style={{ color: "var(--accent-red)" }}>*</span>}</label>}
      <select name={name} value={value} onChange={onChange} required={required}
        style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "10px 14px", color: "var(--text-primary)", fontSize: 14, fontFamily: "var(--font-body)", outline: "none", width: "100%" }}>
        <option value="">-- Select --</option>
        {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    </div>
  );
}

export function Badge({ children, color = "green" }) {
  const colors = {
    green:  { bg: "rgba(37,211,102,0.1)",  text: "#25d366", border: "rgba(37,211,102,0.25)" },
    orange: { bg: "rgba(255,149,0,0.1)",   text: "#ff9500", border: "rgba(255,149,0,0.25)" },
    blue:   { bg: "rgba(77,159,255,0.1)",  text: "#4d9fff", border: "rgba(77,159,255,0.25)" },
    red:    { bg: "rgba(255,77,77,0.1)",   text: "#ff4d4d", border: "rgba(255,77,77,0.25)" },
    grey:   { bg: "rgba(136,136,136,0.1)", text: "#888",    border: "rgba(136,136,136,0.25)" },
  };
  const c = colors[color] || colors.grey;
  return <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>{children}</span>;
}

export function Toast({ message, type = "success", onClose }) {
  if (!message) return null;
  const colors = {
    success: { bg: "rgba(37,211,102,0.1)", border: "rgba(37,211,102,0.3)", text: "var(--accent)" },
    error:   { bg: "rgba(255,77,77,0.1)",  border: "rgba(255,77,77,0.3)",  text: "#ff4d4d" },
  };
  const c = colors[type];
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 1000, background: c.bg, border: `1px solid ${c.border}`, borderRadius: "var(--radius-lg)", padding: "12px 20px", color: c.text, fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center", gap: 10, boxShadow: "var(--shadow)", animation: "fadeIn 0.3s ease" }}>
      <span>{type === "success" ? "✓" : "✕"}</span>
      <span>{message}</span>
      <button onClick={onClose} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: 16 }}>×</button>
    </div>
  );
}

export function Loading() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--text-muted)", padding: "40px 0" }}>
      <div style={{ width: 16, height: 16, border: "2px solid var(--border)", borderTop: "2px solid var(--accent)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <span style={{ fontSize: 13 }}>Loading...</span>
    </div>
  );
}

export function Empty({ message = "No data found", icon = "◎" }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", color: "var(--text-muted)", gap: 10 }}>
      <span style={{ fontSize: 32 }}>{icon}</span>
      <span style={{ fontSize: 13 }}>{message}</span>
    </div>
  );
}

export function SectionHeader({ title, subtitle, action }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
      <div>
        <h2 style={{ fontSize: 16, fontWeight: 600, fontFamily: "var(--font-mono)" }}>{title}</h2>
        {subtitle && <p style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 4 }}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
